import React, { createContext, useState, useCallback } from "react";
import type { ReactNode } from "react";
import { Spinner } from "flowbite-react";

// Busy context type
interface BusyContextType {
  isBusy: boolean;
  setBusy: (busy: boolean, message?: string) => void;
  withBusy: <T>(promise: Promise<T>, message?: string) => Promise<T>;
}

// Create the busy context
export const BusyContext = createContext<BusyContextType | null>(null);

// Provider props
interface BusyProviderProps {
  children: ReactNode;
  spinnerSize?: "sm" | "md" | "lg" | "xl";
  overlayColor?: string;
  message?: string;
}

// Busy Provider component
export const BusyProvider: React.FC<BusyProviderProps> = ({
  children,
  spinnerSize = "xl",
  overlayColor = "rgba(0, 0, 0, 0.5)",
  message = "Loading...",
}) => {
  const [isBusy, setIsBusy] = useState(false);
  const [busyMessage, setBusyMessage] = useState(message);

  // Set busy state
  const setBusy = useCallback(
    (busy: boolean, customMessage?: string) => {
      setIsBusy(busy);
      if (customMessage) {
        setBusyMessage(customMessage);
      } else {
        setBusyMessage(message);
      }
    },
    [message]
  );

  // Wrapper function to automatically handle busy state for promises
  const withBusy = useCallback(
    async <T,>(promise: Promise<T>, customMessage?: string): Promise<T> => {
      setBusy(true, customMessage);
      try {
        const result = await promise;
        return result;
      } finally {
        setBusy(false);
      }
    },
    [setBusy]
  );

  const value: BusyContextType = {
    isBusy,
    setBusy,
    withBusy,
  };

  return (
    <BusyContext.Provider value={value}>
      {children}

      {/* Global Busy Overlay */}
      {isBusy && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ backgroundColor: overlayColor }}
        >
          <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col items-center space-y-4 min-w-[200px]">
            <Spinner size={spinnerSize} />
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">
                {busyMessage}
              </h3>
              <p className="text-sm text-gray-600 mt-1">Please wait...</p>
            </div>
          </div>
        </div>
      )}
    </BusyContext.Provider>
  );
};

export default BusyProvider;
